import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  Link,
} from '@tanstack/react-router';
import { AuthProvider, useAuth } from '@/lib/auth';
import { useLeadsLive, type LeadScope } from '@/lib/leads';
import { Shell, Page, PageHeader } from '@/components/Shell';
import { Booting, GateScreen } from '@/screens/Gate';
import { TodayScreen } from '@/screens/Today';
import { EnquiriesScreen, NewLeadScreen } from '@/screens/Enquiries';
import { LeadScreen, type Composer } from '@/screens/Lead';
import { ClientsScreen, MoneyScreen, MoreScreen, SettingsScreen } from '@/screens/Other';
import { WorkScreen } from '@/screens/Work';
import { CaseStudyEditorScreen } from '@/screens/CaseStudyEditor';
import { LabEditorScreen } from '@/screens/LabEditor';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: true },
  },
});

/* ------------------------------------------------------------------ routes */

function Root() {
  const { gate } = useAuth();
  if (gate.step === 'loading') return <Booting />;
  if (gate.step !== 'ready') return <GateScreen gate={gate} />;
  return <Ready />;
}

/** Signed in as staff: the app, kept live. */
function Ready() {
  useLeadsLive();
  return (
    <Shell>
      <Outlet />
    </Shell>
  );
}

const rootRoute = createRootRoute({
  component: Root,
  notFoundComponent: () => (
    <>
      <PageHeader title="Not here" />
      <Page>
        <p className="text-fg-dim">There is no page at this address.</p>
        <Link to="/" className="btn btn-quiet mt-4">
          Back to Today
        </Link>
      </Page>
    </>
  ),
});

const SCOPES: LeadScope[] = ['open', 'won', 'closed', 'all'];
const REACH: Exclude<Composer, null>[] = ['whatsapp', 'email', 'call'];

const enquiriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/enquiries',
  validateSearch: (s: Record<string, unknown>): { scope?: LeadScope; q?: string } => ({
    scope: SCOPES.includes(s.scope as LeadScope) ? (s.scope as LeadScope) : undefined,
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
  }),
  component: EnquiriesRoute,
});
function EnquiriesRoute() {
  const { scope = 'open', q = '' } = enquiriesRoute.useSearch();
  return <EnquiriesScreen key={scope} scope={scope} q={q} />;
}

const leadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/enquiries/$leadId',
  validateSearch: (s: Record<string, unknown>): { reach?: Exclude<Composer, null> } => ({
    reach: REACH.includes(s.reach as never) ? (s.reach as Exclude<Composer, null>) : undefined,
  }),
  component: LeadRoute,
});
function LeadRoute() {
  const { leadId } = leadRoute.useParams();
  const { reach } = leadRoute.useSearch();
  return <LeadScreen key={leadId} leadId={leadId} reach={reach} />;
}

const caseStudyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work/$caseStudyId',
  component: function CaseStudyRoute() {
    const { caseStudyId } = caseStudyRoute.useParams();
    return <CaseStudyEditorScreen key={caseStudyId} id={caseStudyId} />;
  },
});

const labRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work/lab/$labId',
  component: function LabRoute() {
    const { labId } = labRoute.useParams();
    return <LabEditorScreen key={labId} id={labId} />;
  },
});

const page = <P extends string>(path: P, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => rootRoute, path, component });

const routeTree = rootRoute.addChildren([
  page('/', TodayScreen),
  enquiriesRoute,
  page('/enquiries/new', NewLeadScreen),
  leadRoute,
  page('/clients', ClientsScreen),
  page('/work', WorkScreen),
  caseStudyRoute,
  labRoute,
  page('/money', MoneyScreen),
  page('/settings', SettingsScreen),
  page('/more', MoreScreen),
]);

const router = createRouter({ routeTree, defaultPreload: 'intent', scrollRestoration: true });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
